import { useMemo, useState, useEffect, useCallback, useReducer } from 'react';

import useQuestionSearchColumns from './useQuestionSearchColumns';

import { useGetAdvancedQuestionSearch } from 'hooks';

import { showErrorToast } from 'utils/showToast';
import {
  questionColumnData,
  searchItem,
  sortItem,
  questionInitialState,
} from 'utils/advanceSearchUtils';

import {
  QuestionSearchParams,
  SearchRuleItem,
  QuestionOptionsType,
} from '../../advancedSearchTypes';

import useAdvancedSearchStore from 'store/useAdvancedSearchStore';

function reducer(
  state: QuestionOptionsType,
  action: {
    type: keyof QuestionOptionsType | 'reset';
    newState?: QuestionOptionsType;
  },
) {
  switch (action.type) {
    case 'includeGlobal': {
      return {
        ...state,
        includeGlobal: !state.includeGlobal,
      };
    }
    case 'includeInactive': {
      return {
        ...state,
        includeInactive: !state.includeInactive,
      };
    }
    case 'includeHidden': {
      return {
        ...state,
        includeHidden: !state.includeHidden,
      };
    }
    case 'reset':
      if (action.newState) return { ...action.newState };
      return { ...questionInitialState };
    default:
      throw new Error();
  }
}
const initialSearchList: Array<SearchRuleItem> = [{ ...searchItem }];

export default function useQuestionSearch() {
  // search block states
  const [searchList, setSearchList] = useState<Array<SearchRuleItem>>([
    ...initialSearchList.map(item => item),
  ]);
  const [searchParams, setSearchParams] = useState<QuestionSearchParams>();
  const [columnsSearch, setColumnsSearch] = useState({}); // header search
  const [currentSortItem, setCurrentSortItem] = useState(sortItem);

  //checkbox states
  const [questionOptions, setQuestionOptions] = useReducer(reducer, {
    ...questionInitialState,
  });

  const { searchStore, setParams } = useAdvancedSearchStore();

  useEffect(() => {
    if (searchStore) {
      if (searchStore.questionSearchParams) {
        const { includeGlobal, includeInactive, searchList, includeHidden } =
          searchStore.questionSearchParams;
        setQuestionOptions({
          type: 'reset',
          newState: {
            includeGlobal,
            includeInactive,
            includeHidden,
          },
        });
        setSearchList(searchList);
      }
      if (searchStore.sortItem) {
        setCurrentSortItem(searchStore.sortItem);
      }
      if (searchStore.columnSearch) {
        setColumnsSearch(searchStore.columnSearch);
      }
      setSearchParams(searchStore.questionSearchParams);
    }
  }, [searchStore]);

  const {
    data,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: advancedSearchloading,
  } = useGetAdvancedQuestionSearch(
    searchParams,
    columnsSearch,
    currentSortItem,
  );

  const tableData = useMemo(() => {
    if (!data?.pages) return [];

    return data.pages.flatMap((page: any) =>
      (page?.Data?.Data ?? []).map((item: any) => ({
        ...item,
        FieldLabel: item.QuestionDisplayName ?? '',
      }))
    );
  }, [data]);
  const currentRecords = useMemo(() => {
    if (data) {
      return data.pages.map((dataItem: any) => dataItem?.Data?.Data)?.flat();
    }
  }, [data]);

  const totalRecords = useMemo(() => {
    if (data) {
      return data.pages[0] && data.pages[0]?.Data?.TotalRecords;
    }
    return 0;
  }, [data]);

  const csvImportData = tableData?.length ? tableData : [];

  const questionsDataCsv = [
    [
      'AccountId',
      'CID',
      'PID',
      'SID',
      'AccountName',
      'FieldName',
      'FieldLabel',
      'QuestionType',
      'SectionTitle',
    ],
    ...csvImportData.map(
      ({
        AccountId,
        CID,
        PID,
        SID,
        AccountName,
        FieldName,
        FieldLabel,
        QuestionType,
        SectionTitle,
      }: any) => [
          AccountId,
          CID,
          PID,
          SID,
          AccountName,
          FieldName,
          FieldLabel,
          QuestionType,
          SectionTitle,
        ],
    ),
  ];

  const onEndReached = () => {
    if (hasNextPage) fetchNextPage();
  };

  const columnList = useQuestionSearchColumns([...questionColumnData.slice()]);

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const resetQuestionSearch = () => {
    setSearchList([{ ...searchItem }]);
    setColumnsSearch({});
    setCurrentSortItem(sortItem);
    setQuestionOptions({ type: 'reset' });
    setSearchParams(undefined);
  };

  const toggleQuestionOption = (key?: keyof QuestionOptionsType) => () => {
    if (!key) return setQuestionOptions({ type: 'reset' });
    return setQuestionOptions({ type: key });
  };

  function searchButtonClick() {
    if (searchList.filter(item => item.value.trim().length === 0).length > 0) {
      showErrorToast({
        message: 'Please Enter a keyword',
      });
    } else {
      setSearchParams({
        searchList: JSON.parse(JSON.stringify(searchList.slice())),
        ...questionOptions,
      });
    }
  }

  function onClickToolTip(
    path: string,
    params: Record<string, number | string>,
  ) {
    setParams({
      questionSearchParams: searchParams,
      columnSearch: columnsSearch,
      sortItem: currentSortItem,
    });
    localStorage.setItem('fromSearch', JSON.stringify(params?.Pid));
    window.open(path);
  }

  return {
    questionsDataCsv,
    totalQuestionRecordsCount: totalRecords,
    listedQuestionRecordCount: currentRecords,
    isQuestionSearchFetching: isFetching,
    questionOptions,
    toggleQuestionOption,
    resetQuestionSearch,
    questionTableProps: {
      columns: columnList,
      columnsSearch: columnsSearch,
      data: tableData,
      headerUpdateCallBack: headerUpdateCallBack,
      sortedItem: currentSortItem,
      handleSortChange: handleSortChange,
      isLoading: advancedSearchloading,
      noOptions: true,
      onEndReached: onEndReached,
      isFetching: !!isFetchingNextPage,
      onRedirect: onClickToolTip,
    },
    questionSearch: {
      searchList,
      setSearchList,
      searchButtonClick,
    },
  };
}
